import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Target, Activity, ClipboardCheck, BookOpen, Trophy, ArrowRight, CheckCircle, MessageCircle, Volume2,
} from '../../components/icons';
import {
  fetchGoals, fetchHabits, fetchTasks, fetchCheckIns, fetchJournalEntries, fetchMilestones,
  type Goal, type Habit, type Task, type CheckIn, type JournalEntry, type Milestone,
} from '../../lib/progress';
import { fetchMyAICoachAccess, fetchCoachConfigForRespondent } from '../../lib/ai-coach';
import { AICoachChat } from '../../components/ai-coach/AICoachChat';
import { AICoachVoice } from '../../components/ai-coach/AICoachVoice';
import { supabase } from '../../lib/supabase';

const today = new Date().toISOString().split('T')[0];

function avg(nums: number[]) {
  if (!nums.length) return 0;
  return Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 10) / 10;
}

interface SectionCardProps {
  icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  title: string;
  href: string;
  children: React.ReactNode;
}

function SectionCard({ icon: Icon, iconBg, iconColor, title, href, children }: SectionCardProps) {
  return (
    <div className="freud-card-elevated p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 ${iconBg} rounded-xl flex items-center justify-center`}>
            <Icon className={`w-4 h-4 ${iconColor}`} />
          </div>
          <h2 className="freud-heading-base text-chocolate-900">{title}</h2>
        </div>
        <Link to={href} className="flex items-center gap-1 text-xs font-medium text-sage-600 hover:text-sage-700 transition-colors">
          View all <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <div>{children}</div>
    </div>
  );
}

function StatPill({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${color}`}>
      {value} <span className="font-normal opacity-80">{label}</span>
    </span>
  );
}

function EmptyState({ label }: { label: string }) {
  return <p className="text-sm text-chocolate-400 italic">{label}</p>;
}

export function ProgressOverview() {
  const { user } = useAuth();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [habits, setHabits] = useState<Habit[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [journal, setJournal] = useState<JournalEntry[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);

  // AI Coach
  const [chatEnabled, setChatEnabled] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [elevenlabsAgentId, setElevenlabsAgentId] = useState('');
  const [respondentName, setRespondentName] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [showVoice, setShowVoice] = useState(false);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      fetchGoals(user.id),
      fetchHabits(user.id),
      fetchTasks(user.id),
      fetchCheckIns(user.id, 7),
      fetchJournalEntries(user.id),
      fetchMilestones(user.id),
    ]).then(([g, h, t, c, j, m]) => {
      setGoals(g);
      setHabits(h);
      setTasks(t);
      setCheckIns(c);
      setJournal(j);
      setMilestones(m);
    }).finally(() => setLoading(false));

    // AI coach access
    const loadAICoach = async () => {
      const { data: profile } = await supabase
        .from('users')
        .select('full_name, email')
        .eq('id', user.id)
        .maybeSingle();
      setRespondentName(profile?.full_name || profile?.email || '');

      const { data: respRecord } = await supabase
        .from('respondents')
        .select('id, coach_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (respRecord?.coach_id) {
        const [access, coachConfig] = await Promise.all([
          fetchMyAICoachAccess(respRecord.id),
          fetchCoachConfigForRespondent(respRecord.coach_id),
        ]);
        if (coachConfig?.isEnabled) {
          setChatEnabled(access ? access.chatEnabled : true);
          setVoiceEnabled(access ? access.voiceEnabled : true);
          setElevenlabsAgentId(coachConfig.elevenlabsAgentId || '');
        }
      }
    };

    loadAICoach();
  }, [user]);

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="freud-card-elevated p-5 h-36 animate-pulse bg-cream-100" />
          ))}
        </div>
      </div>
    );
  }

  // Goals stats
  const activeGoals = goals.filter(g => g.status === 'active');
  const completedGoals = goals.filter(g => g.status === 'completed');

  // Habits stats
  const activeHabits = habits.filter(h => h.is_active);
  const todayDone = activeHabits.filter(h =>
    (h.completions || []).some(c => c.completed_date === today)
  ).length;

  // Tasks stats
  const openTasks = tasks.filter(t => t.status !== 'done');
  const highPriOpen = openTasks.filter(t => t.priority === 'high');
  const nextDue = openTasks.filter(t => t.due_date).sort((a, b) => a.due_date! < b.due_date! ? -1 : 1)[0];

  // Check-ins stats
  const recent7 = checkIns.slice(0, 7);
  const avgEnergy = avg(recent7.map(c => c.energy_level));
  const avgMood = avg(recent7.map(c => c.mood_level));
  const avgFocus = avg(recent7.map(c => c.focus_level));
  const lastCheckIn = checkIns[0];

  // Journal stats
  const lastEntry = journal[0];

  // Milestones stats
  const lastMilestone = milestones[0];

  return (
    <div className="p-6 md:p-8 max-w-5xl">
      <div className="mb-6">
        <h1 className="freud-heading-xl text-chocolate-900">Progress</h1>
        <p className="text-sm text-chocolate-500 mt-1">Your goals, habits, and growth at a glance.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">

        {/* Goals */}
        <SectionCard icon={Target} iconBg="bg-sage-100" iconColor="text-sage-600" title="Goals" href="/dashboard/progress/goals">
          <div className="flex flex-wrap gap-2 mb-3">
            <StatPill value={activeGoals.length} label="active" color="bg-sage-100 text-sage-700" />
            {completedGoals.length > 0 && (
              <StatPill value={completedGoals.length} label="completed" color="bg-blue-100 text-blue-700" />
            )}
          </div>
          {activeGoals.length === 0 ? (
            <EmptyState label="No active goals yet." />
          ) : (
            <ul className="space-y-1.5">
              {activeGoals.slice(0, 3).map(g => (
                <li key={g.id} className="flex items-center gap-2 text-sm text-chocolate-700">
                  <CheckCircle className="w-3.5 h-3.5 text-chocolate-200 flex-shrink-0" />
                  <span className="truncate">{g.title}</span>
                </li>
              ))}
              {activeGoals.length > 3 && (
                <li className="text-xs text-chocolate-400">+{activeGoals.length - 3} more</li>
              )}
            </ul>
          )}
        </SectionCard>

        {/* Habits */}
        <SectionCard icon={Activity} iconBg="bg-coral-100" iconColor="text-coral-600" title="Habits" href="/dashboard/progress/habits">
          <div className="flex flex-wrap gap-2 mb-3">
            <StatPill value={activeHabits.length} label="active" color="bg-coral-100 text-coral-700" />
            <StatPill value={`${todayDone}/${activeHabits.length}`} label="done today" color="bg-sage-100 text-sage-700" />
          </div>
          {activeHabits.length === 0 ? (
            <EmptyState label="No habits yet." />
          ) : (
            <div className="space-y-1.5">
              {activeHabits.slice(0, 3).map(h => {
                const done = (h.completions || []).some(c => c.completed_date === today);
                return (
                  <div key={h.id} className="flex items-center gap-2 text-sm">
                    <div className={`w-3.5 h-3.5 rounded flex-shrink-0 flex items-center justify-center ${done ? 'bg-sage-500' : 'bg-cream-200'}`}>
                      {done && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                    </div>
                    <span className={`truncate ${done ? 'text-chocolate-400 line-through' : 'text-chocolate-700'}`}>{h.title}</span>
                  </div>
                );
              })}
              {activeHabits.length > 3 && (
                <p className="text-xs text-chocolate-400">+{activeHabits.length - 3} more</p>
              )}
            </div>
          )}
        </SectionCard>

        {/* Tasks */}
        <SectionCard icon={ClipboardCheck} iconBg="bg-amber-100" iconColor="text-amber-600" title="Tasks" href="/dashboard/progress/tasks">
          <div className="flex flex-wrap gap-2 mb-3">
            <StatPill value={openTasks.length} label="open" color="bg-amber-100 text-amber-700" />
            {highPriOpen.length > 0 && (
              <StatPill value={highPriOpen.length} label="high priority" color="bg-red-100 text-red-600" />
            )}
          </div>
          {openTasks.length === 0 ? (
            <p className="text-sm text-chocolate-400 italic">All caught up!</p>
          ) : (
            <div className="space-y-1.5">
              {openTasks.slice(0, 3).map(t => (
                <div key={t.id} className="flex items-center gap-2 text-sm text-chocolate-700">
                  <div className={`w-3.5 h-3.5 rounded border-2 flex-shrink-0 ${t.status === 'in_progress' ? 'border-amber-400 bg-amber-50' : 'border-chocolate-300'}`} />
                  <span className="truncate">{t.title}</span>
                  {t.due_date && (
                    <span className="text-[10px] text-chocolate-400 flex-shrink-0">
                      {new Date(t.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
              ))}
              {nextDue && openTasks.length > 3 && (
                <p className="text-xs text-chocolate-400">+{openTasks.length - 3} more</p>
              )}
            </div>
          )}
        </SectionCard>

        {/* Check-ins */}
        <SectionCard icon={Activity} iconBg="bg-blue-100" iconColor="text-blue-600" title="Check-ins" href="/dashboard/progress/check-ins">
          {checkIns.length === 0 ? (
            <EmptyState label="No check-ins yet." />
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-chocolate-400">
                Last: {new Date(lastCheckIn.checked_in_at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
              </p>
              {recent7.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Energy', value: avgEnergy, color: 'bg-amber-400' },
                    { label: 'Mood', value: avgMood, color: 'bg-coral-400' },
                    { label: 'Focus', value: avgFocus, color: 'bg-blue-400' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="text-center">
                      <div className="text-lg font-bold text-chocolate-800">{value}</div>
                      <div className="h-1.5 rounded-full bg-cream-200 mt-1 overflow-hidden">
                        <div className={`h-full rounded-full ${color}`} style={{ width: `${(value / 10) * 100}%` }} />
                      </div>
                      <div className="text-[10px] text-chocolate-400 mt-0.5">{label}</div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-chocolate-400">7-day average</p>
            </div>
          )}
        </SectionCard>

        {/* Journal */}
        <SectionCard icon={BookOpen} iconBg="bg-purple-100" iconColor="text-purple-600" title="Journal" href="/dashboard/progress/journal">
          <div className="flex flex-wrap gap-2 mb-3">
            <StatPill value={journal.length} label="entries" color="bg-purple-100 text-purple-700" />
          </div>
          {journal.length === 0 ? (
            <EmptyState label="No journal entries yet." />
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-medium text-chocolate-700 truncate">{lastEntry.title || 'Untitled'}</p>
              <p className="text-xs text-chocolate-400">
                {new Date(lastEntry.created_at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
              </p>
              {lastEntry.content && (
                <p className="text-xs text-chocolate-500 line-clamp-2">{lastEntry.content}</p>
              )}
            </div>
          )}
        </SectionCard>

        {/* Milestones */}
        <SectionCard icon={Trophy} iconBg="bg-yellow-100" iconColor="text-yellow-600" title="Milestones" href="/dashboard/progress/milestones">
          <div className="flex flex-wrap gap-2 mb-3">
            <StatPill value={milestones.length} label="total" color="bg-yellow-100 text-yellow-700" />
          </div>
          {milestones.length === 0 ? (
            <EmptyState label="No milestones yet." />
          ) : (
            <div className="space-y-1.5">
              {milestones.slice(0, 3).map(m => (
                <div key={m.id} className="flex items-start gap-2 text-sm">
                  <Trophy className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-chocolate-700 truncate">{m.title}</p>
                    <p className="text-[10px] text-chocolate-400">
                      {new Date(m.achieved_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                </div>
              ))}
              {milestones.length > 3 && (
                <p className="text-xs text-chocolate-400">+{milestones.length - 3} more</p>
              )}
            </div>
          )}
        </SectionCard>

      </div>

      {/* AI Coach floating buttons */}
      {(chatEnabled || voiceEnabled) && (
        <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-40">
          {voiceEnabled && (
            <button
              onClick={() => setShowVoice(true)}
              className="w-12 h-12 rounded-full bg-warm-500 text-white shadow-lg hover:bg-warm-600 transition-colors flex items-center justify-center"
              title="Talk to your AI Coach"
            >
              <Volume2 className="w-5 h-5" />
            </button>
          )}
          {chatEnabled && (
            <button
              onClick={() => setShowChat(true)}
              className="w-12 h-12 rounded-full bg-sage-600 text-white shadow-lg hover:bg-sage-700 transition-colors flex items-center justify-center"
              title="Chat with your AI Coach"
            >
              <MessageCircle className="w-5 h-5" />
            </button>
          )}
        </div>
      )}

      {chatEnabled && (
        <AICoachChat
          isOpen={showChat}
          onClose={() => setShowChat(false)}
          pathwayContext="I'm reviewing my overall progress — goals, habits, tasks, and check-ins. I'd like support and guidance on where to focus."
        />
      )}

      {voiceEnabled && (
        <AICoachVoice
          isOpen={showVoice}
          onClose={() => setShowVoice(false)}
          agentId={elevenlabsAgentId}
          respondentName={respondentName}
          scores={[]}
          harmonicScores={[]}
        />
      )}
    </div>
  );
}
